import type { Msg, Subscription } from "@nats-io/nats-core";
import type { RealtimeMessage, ClassroomRealtimeMessage } from "@web-monorepo/api-types/zodSchemas/realtime/index";

export type RealtimeMsg = Pick<Msg, "subject" | "data">;

export type RealtimeSubscription = Pick<Subscription, "unsubscribe" | "drain"> & AsyncIterable<RealtimeMsg>;

export interface RealtimeAdapter {
  init(): Promise<void>;
  subscribe(topic: string): RealtimeSubscription;
  unsubscribe(topic: string): void;
  publish(topic: string, data: Uint8Array): void;
  close(): Promise<void>;
}

export interface RealtimeMetrics {
  increment(metricName: string, tags?: Record<string, string>): void;
  // Numeric distribution (percentiles/histogram in Datadog). Used for connection
  // downtime (C3) so we can read p50/p95 blip length and pick reconnect budgets
  // from data rather than guesses. Optional so existing callers/mocks that only
  // implement increment keep compiling.
  distribution?(metricName: string, value: number, tags?: Record<string, string>): void;
}

export type RealtimePayload = RealtimeMessage | ClassroomRealtimeMessage;
export type { RealtimeMessage };

export type RealtimeMessageHandler<T extends RealtimePayload = RealtimePayload> = (message: T) => void | Promise<void>;

// Client network state at the moment of a failure capture, used to split
// "device was offline" from "online but the path to NATS is blocked" in Sentry.
// Returned as low-cardinality strings so they can be attached as Sentry TAGS
// (searchable/groupable — enables the offline-vs-blocked split on the issue page),
// not extra (which Sentry can't facet on). SSR-guarded: navigator/document are
// undefined outside the browser.
export function getClientNetworkState(): {
  navigatorOnLine: "true" | "false" | "unknown";
  visibilityState: DocumentVisibilityState | "unknown";
} {
  return {
    navigatorOnLine: typeof navigator === "undefined" ? "unknown" : navigator.onLine ? "true" : "false",
    visibilityState: typeof document === "undefined" ? "unknown" : document.visibilityState,
  };
}

export const NATS_EVER_CONNECTED_STORAGE_KEY = "dojo_nats_ever_connected";
export const NATS_FLAG_CREATED_AT_STORAGE_KEY = "dojo_nats_flag_created_at";

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
const MONTH_MS = 30 * DAY_MS;
// Anything below this doesn't parse as a plausible epoch-ms "now" — catches the C4-era
// tombstone value ("1") and other garbage. Corresponds to 2020-09-13, comfortably before
// this flag existed.
const MIN_PLAUSIBLE_EPOCH_MS = 1_600_000_000_000;

// Device-level connection history, used alongside getClientNetworkState() to split the
// recovery-exhausted Sentry issue into "blip" (transient hiccup) vs "chronically blocked"
// (WebSocket never works on this device — school middlebox territory) populations.
// localStorage can THROW (not just return null) in some private-browsing modes and under
// school storage policies (SecurityError), so all functions below swallow that failure
// rather than let it break the capture path they support.
//
// C4's day-1 read found the no-TTL and storage-wipe biases below could each skew the
// device="true"/"false" split by double-digit percentage points, in opposite directions:
//
// - NO TTL (undercounts "chronically blocked"): a one-time "1" tombstone means a personal
//   laptop that connected once months ago still reads device="true" even after moving onto
//   a now-blocking network. Fixed by lastConnectedOnThisDevice (getLastConnectedBucket): the
//   flag is now a refreshed timestamp, bucketed by recency, so a stale "true" ages out.
// - STORAGE WIPES (overcounts "chronically blocked"): managed school browsers that clear
//   site data nightly re-read device="false" even for a device that connected yesterday.
//   Can't be prevented client-side, but deviceFlagAge (getDeviceFlagAgeBucket) measures it:
//   if "false" events are persistently deviceFlagAge="under_24h", that's nightly wipes, not
//   a chronically blocked network — Population 2 can be corrected arithmetically.

// Sentinel distinguishing "storage threw / SSR" from "key absent" — the two states that
// map to different bucket values (`unavailable` vs `never`/`legacy`) below.
const STORAGE_UNAVAILABLE = Symbol("storage-unavailable");

/** Reads `key` from localStorage. Returns the raw string, null if absent, or the
 * STORAGE_UNAVAILABLE sentinel on SSR or if storage throws. */
function readRawStorage(key: string): string | null | typeof STORAGE_UNAVAILABLE {
  if (typeof window === "undefined") {
    return STORAGE_UNAVAILABLE;
  }
  try {
    // eslint-disable-next-line @web-monorepo/no-browser-storage -- guarded by try/catch below; SSR-guarded above
    return window.localStorage.getItem(key);
    // eslint-disable-next-line no-catch-all/no-catch-all -- localStorage can throw (private mode, storage policy)
  } catch {
    return STORAGE_UNAVAILABLE;
  }
}

/** Parses a stored value into a plausible epoch-ms timestamp, or null if absent/implausible
 * (catches the C4-era tombstone value `"1"` and other garbage). */
function parsePlausibleEpochMs(stored: string | null): number | null {
  if (!stored) {
    return null;
  }
  const parsed = Number(stored);
  return Number.isFinite(parsed) && parsed >= MIN_PLAUSIBLE_EPOCH_MS ? parsed : null;
}

function writeTimestamp(key: string, timestampMs: number): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    // eslint-disable-next-line @web-monorepo/no-browser-storage -- guarded by try/catch below; SSR-guarded above
    window.localStorage.setItem(key, String(timestampMs));
    // eslint-disable-next-line no-catch-all/no-catch-all -- localStorage can throw (private mode, storage policy); best-effort marker
  } catch {
    // Swallow: this is a best-effort marker, not a critical write.
  }
}

/** Buckets a timestamp's age against `now`, clamping future timestamps (clock skew) into
 * `under_24h` rather than inventing a new bucket. */
function bucketAge(timestampMs: number, now: number): "under_24h" | "d1_7" | "d7_30" | "over_30d" {
  const ageMs = Math.max(0, now - timestampMs);
  if (ageMs < DAY_MS) {
    return "under_24h";
  }
  if (ageMs < WEEK_MS) {
    return "d1_7";
  }
  if (ageMs < MONTH_MS) {
    return "d7_30";
  }
  return "over_30d";
}

/**
 * Marks a successful connect on this device. Refreshes the stored timestamp, throttled to
 * at most once per 24h so kiosk-style sessions that reconnect daily don't skip the refresh
 * and drift into stale buckets, while still avoiding a synchronous write on every reconnect.
 */
export function markEverConnectedOnThisDevice(): void {
  const stored = readRawStorage(NATS_EVER_CONNECTED_STORAGE_KEY);
  if (stored === STORAGE_UNAVAILABLE) {
    return;
  }
  const existing = parsePlausibleEpochMs(stored);
  if (existing !== null && Date.now() - existing < DAY_MS) {
    return;
  }
  writeTimestamp(NATS_EVER_CONNECTED_STORAGE_KEY, Date.now());
  // Belt-and-suspenders: ensureFlagCreatedAt() already fires at hook init, but call it here
  // too so flag_created_at is never missing if this ever runs outside that lifecycle.
  ensureFlagCreatedAt();
}

/**
 * Stamps `flag_created_at` the first time it's read, so `deviceFlagAge` is meaningful even
 * for devices that never successfully connect. Written once, ever — never updated.
 */
export function ensureFlagCreatedAt(): void {
  const stored = readRawStorage(NATS_FLAG_CREATED_AT_STORAGE_KEY);
  if (stored === STORAGE_UNAVAILABLE || stored) {
    return;
  }
  writeTimestamp(NATS_FLAG_CREATED_AT_STORAGE_KEY, Date.now());
}

export function getEverConnectedOnThisDevice(): "true" | "false" | "unavailable" {
  const stored = readRawStorage(NATS_EVER_CONNECTED_STORAGE_KEY);
  if (stored === STORAGE_UNAVAILABLE) {
    return "unavailable";
  }
  return stored ? "true" : "false";
}

/**
 * Ages `dojo_nats_ever_connected` into a recency bucket for the Sentry facet. A C4-era
 * stored `"1"` doesn't parse as a plausible timestamp, so it lands in `legacy` and
 * self-heals to a real timestamp on the device's next successful connect.
 */
export function getLastConnectedBucket():
  | "under_24h"
  | "d1_7"
  | "d7_30"
  | "over_30d"
  | "legacy"
  | "never"
  | "unavailable" {
  const stored = readRawStorage(NATS_EVER_CONNECTED_STORAGE_KEY);
  if (stored === STORAGE_UNAVAILABLE) {
    return "unavailable";
  }
  if (!stored) {
    return "never";
  }
  const parsed = parsePlausibleEpochMs(stored);
  if (parsed === null) {
    return "legacy";
  }
  return bucketAge(parsed, Date.now());
}

/**
 * Ages `dojo_nats_flag_created_at` into a recency bucket for the Sentry facet. `under_24h`
 * on a `false` device population is the storage-wipe signal: the flag was just re-created,
 * meaning site data was recently cleared rather than the network being chronically blocked.
 */
export function getDeviceFlagAgeBucket(): "under_24h" | "d1_7" | "over_7d" | "unavailable" {
  const stored = readRawStorage(NATS_FLAG_CREATED_AT_STORAGE_KEY);
  if (stored === STORAGE_UNAVAILABLE || !stored) {
    return "unavailable";
  }
  const parsed = parsePlausibleEpochMs(stored);
  if (parsed === null) {
    return "unavailable";
  }
  const bucket = bucketAge(parsed, Date.now());
  // Flag age only needs to signal "recently wiped" (under_24h / d1_7) vs "old" (over_7d);
  // finer resolution beyond 7d isn't useful for the storage-wipe detection use-case.
  return bucket === "d7_30" || bucket === "over_30d" ? "over_7d" : bucket;
}

export function getPermissionViolationFingerprint(error: unknown): string[] | undefined {
  if (
    error instanceof Error &&
    (error.name.includes("PermissionViolation") || error.message.includes("PermissionViolation"))
  ) {
    return ["nats-permission-violation"];
  }
  return undefined;
}
